// client/src/pages/OperatorsDashboard/Withdrawfunds.tsx
import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import { kxtService } from "@/api/services/kxtService";
import { useTranslation } from "react-i18next";
import { KXT_REDEMPTION_ENABLED } from "@/lib/featureFlags";
import FiatWithdrawPanel from "@/components/FiatWithdrawPanel";

export default function WithdrawFunds() {
  const { theme } = useApp();
  const { t } = useTranslation();

  // Balance
  const [kxtBalance, setKxtBalance] = useState<number>(0);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);

  // Form
  const [amount, setAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<'bank_transfer' | 'mobile_money' | 'crypto'>('crypto');
  const [withdrawalDetails, setWithdrawalDetails] = useState({
    bankName: '',
    accountName: '',
    bankNumber: '',
    phoneNumber: '',
    cryptoWalletAddress: '',
  });

  // Withdrawal history
  const [withdrawals, setWithdrawals] = useState<{ _id: string; amount: number; withdrawalMethod?: string; createdAt: string; status: string }[]>([]);
  const [withdrawalPage, setWithdrawalPage] = useState(1);
  const [withdrawalPagination, setWithdrawalPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNext: false,
    hasPrevious: false,
  });

  // UI
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const FEE_PERCENT = 0;

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 6000);
  };

  // ── Fetch balance and withdrawal history ─────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceData, withdrawalsData] = await Promise.allSettled([
        kxtService.getBalance(),
        kxtService.getWithdrawals(withdrawalPage, 10),
      ]);

      if (balanceData.status === 'fulfilled') {
        setKxtBalance(balanceData.value.kxtBalance || 0);
        setWalletAddress(balanceData.value.kxtWalletAddress);
      }

      if (withdrawalsData.status === 'fulfilled') {
        setWithdrawals(withdrawalsData.value.withdrawals || []);
        setWithdrawalPagination(withdrawalsData.value.pagination);
      }
    } catch (err: unknown) {
      console.error('Error fetching withdrawal data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [withdrawalPage]);

  // ── Submit withdrawal ────────────────────────────────────────────────
  const handleSubmit = async () => {
    const parsedAmount = parseFloat(amount);

    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      showMessage('error', 'Please enter a valid amount');
      return;
    }

    if (parsedAmount < 10) {
      showMessage('error', 'Minimum withdrawal is 10 KXT');
      return;
    }

    if (parsedAmount > kxtBalance) {
      showMessage('error', `Insufficient balance. Available: ${kxtBalance.toFixed(2)} KXT`);
      return;
    }

    // Validate method-specific fields
    if (selectedMethod === 'bank_transfer' && (!withdrawalDetails.bankName || !withdrawalDetails.accountName || !withdrawalDetails.bankNumber)) {
      showMessage('error', 'Please enter your bank name, account name and bank number');
      return;
    }
    if (selectedMethod === 'mobile_money' && !withdrawalDetails.phoneNumber) {
      showMessage('error', 'Please enter your phone number');
      return;
    }
    if (selectedMethod === 'crypto' && !withdrawalDetails.cryptoWalletAddress) {
      showMessage('error', 'Please enter your wallet address');
      return;
    }

    setSubmitting(true);
    try {
      const details = selectedMethod === 'bank_transfer'
        ? {
            bankName: withdrawalDetails.bankName,
            accountName: withdrawalDetails.accountName,
            bankNumber: withdrawalDetails.bankNumber,
          }
        : selectedMethod === 'mobile_money'
          ? { phoneNumber: withdrawalDetails.phoneNumber }
          : { walletAddress: withdrawalDetails.cryptoWalletAddress };

      const result = await kxtService.submitWithdrawal({
        amount: parsedAmount,
        withdrawalMethod: selectedMethod,
        withdrawalDetails: details,
      });

      showMessage('success', `Withdrawal of ${parsedAmount} KXT submitted! Pending admin approval.`);
      setAmount('');
      setWithdrawalDetails({ bankName: '', accountName: '', bankNumber: '', phoneNumber: '', cryptoWalletAddress: '' });
      await fetchData();
    } catch (err: unknown) {
      showMessage('error', (err as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to submit withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Status badge ─────────────────────────────────────────────────────
  const getStatusBadge = (status: string) => {
    const base = 'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium';
    switch (status) {
      case 'completed': return <span className={`${base} bg-green-100 text-green-700`}><CheckCircle className="w-3 h-3" />Completed</span>;
      case 'pending':   return <span className={`${base} bg-yellow-100 text-yellow-700`}><Clock className="w-3 h-3" />Pending</span>;
      case 'processing': return <span className={`${base} bg-blue-100 text-blue-700`}><RefreshCw className="w-3 h-3 animate-spin" />Processing</span>;
      case 'rejected':  return <span className={`${base} bg-red-100 text-red-700`}><XCircle className="w-3 h-3" />Rejected</span>;
      default:          return <span className={`${base} bg-gray-100 text-gray-700`}>{status}</span>;
    }
  };

  const fee = (parseFloat(amount) || 0) * FEE_PERCENT;
  const total = (parseFloat(amount) || 0) - fee;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // KXT redemption/withdrawal is OFF for the beta (counsel, KX-LEG-KXT-001 Q1–Q2;
  // KX-111 item 9). This entire page is a KXT withdrawal control, so it is gated
  // out until counsel/KX-62/KX-45 clear it. KXT remains "accrued rewards" only.
  if (!KXT_REDEMPTION_ENABLED) {
    return (
      <>
        <Helmet>
          <title>{t('operator.withdrawFundsPage.metaTitle', { defaultValue: 'Withdraw Funds | KonectX' })}</title>
        </Helmet>
        <div className={`p-8 ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'} min-h-screen`}>
          <div className="max-w-xl mx-auto space-y-6">
            {/* KX-39: fiat earnings + withdrawal (with KYC-at-payout gate). This is
                the operator's real payout path, separate from KXT. */}
            <FiatWithdrawPanel />

            <div className={`rounded-xl shadow-md p-8 ${theme === 'dark' ? 'bg-[#1a1a1c]' : 'bg-white'}`}>
              <h1 className={`text-xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('operator.withdrawFundsPage.disabledTitle', { defaultValue: 'About KXT' })}
              </h1>
              <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('operator.withdrawFundsPage.disabledBody', {
                  defaultValue:
                    'KXT is an accrued reward for your activity — not a balance, a price or a cash value — and cannot be bought, sold, redeemed or withdrawn. Your fiat earnings are paid out separately.',
                })}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Withdraw Funds | KonectX</title>
        <meta name="description" content="Withdraw your KXT earnings securely." />
      </Helmet>

      <div className={`p-8 ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'} min-h-screen`}>
        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-600'
              : 'bg-red-50 dark:bg-red-900/20 text-red-600'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* ── Left: Withdrawal Form ──────────────────────────────────── */}
          <div className={`rounded-xl shadow-md p-8 ${theme === 'dark' ? 'bg-[#1a1a1c]' : 'bg-white'}`}>
            <h1 className={`text-3xl font-extrabold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Withdraw Funds
            </h1>
            <p className={`text-sm font-medium mb-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Available balance:{' '}
              <span className="text-green-500 font-bold">{kxtBalance.toFixed(2)} KXT</span>
              {walletAddress && (
                <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                  · {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
                </span>
              )}
            </p>

            {/* Amount */}
            <div className="mb-6">
              <label className={`block text-sm font-bold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Amount (KXT)
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter KXT amount (min. 10)"
                className={`w-full px-4 py-3 border-2 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none ${
                  theme === 'dark' ? 'bg-[#2a2a2c] text-white border-gray-700 placeholder-gray-500' : 'bg-white text-gray-900 border-gray-300'
                }`}
              />
              <button
                onClick={() => setAmount(kxtBalance.toString())}
                className="text-xs font-bold text-blue-500 mt-1 hover:text-blue-400"
              >
                Withdraw Max
              </button>
            </div>

            {/* Withdrawal Method */}
            <div className="mb-6">
              <h2 className={`text-base font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Withdrawal Method
              </h2>
              <div className="space-y-2">
                {[
                  { value: 'crypto', label: '💎 Crypto Wallet (CELO / EVM)' },
                  { value: 'bank_transfer', label: '🏦 Bank Transfer' },
                  { value: 'mobile_money', label: '📱 Mobile Money' },
                ].map((method) => (
                  <label key={method.value} className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedMethod === method.value
                      ? theme === 'dark' ? 'bg-[#2a2a2c] border-blue-500' : 'bg-blue-50 border-blue-600'
                      : theme === 'dark' ? 'bg-[#2a2a2c] border-gray-700 hover:border-gray-500' : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="method"
                      value={method.value}
                      checked={selectedMethod === method.value as string}
                      onChange={(e) => setSelectedMethod(e.target.value as never)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className={`ml-3 text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {method.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Method-specific fields */}
            <div className="mb-6 space-y-3">
              {selectedMethod === 'crypto' && (
                <div>
                  <label className={`block text-sm font-bold mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Wallet Address (0x...)
                  </label>
                  <input
                    type="text"
                    value={withdrawalDetails.cryptoWalletAddress}
                    onChange={(e) => setWithdrawalDetails({ ...withdrawalDetails, cryptoWalletAddress: e.target.value })}
                    placeholder="0x..."
                    className={`w-full px-4 py-3 border-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none ${
                      theme === 'dark' ? 'bg-[#2a2a2c] text-white border-gray-700 placeholder-gray-500' : 'bg-white text-gray-900 border-gray-300'
                    }`}
                  />
                </div>
              )}
              {selectedMethod === 'bank_transfer' && (
                <>
                  <div>
                    <label className={`block text-sm font-bold mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Bank Name</label>
                    <input
                      type="text"
                      value={withdrawalDetails.bankName}
                      onChange={(e) => setWithdrawalDetails({ ...withdrawalDetails, bankName: e.target.value })}
                      placeholder="e.g. First Bank"
                      className={`w-full px-4 py-3 border-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none ${
                        theme === 'dark' ? 'bg-[#2a2a2c] text-white border-gray-700 placeholder-gray-500' : 'bg-white text-gray-900 border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-bold mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Account Name</label>
                    <input
                      type="text"
                      value={withdrawalDetails.accountName}
                      onChange={(e) => setWithdrawalDetails({ ...withdrawalDetails, accountName: e.target.value })}
                      placeholder="John Doe"
                      className={`w-full px-4 py-3 border-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none ${
                        theme === 'dark' ? 'bg-[#2a2a2c] text-white border-gray-700 placeholder-gray-500' : 'bg-white text-gray-900 border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-bold mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Account Number</label>
                    <input
                      type="text"
                      value={withdrawalDetails.bankNumber}
                      onChange={(e) => setWithdrawalDetails({ ...withdrawalDetails, bankNumber: e.target.value })}
                      placeholder="0123456789"
                      className={`w-full px-4 py-3 border-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none ${
                        theme === 'dark' ? 'bg-[#2a2a2c] text-white border-gray-700 placeholder-gray-500' : 'bg-white text-gray-900 border-gray-300'
                      }`}
                    />
                  </div>
                </>
              )}
              {selectedMethod === 'mobile_money' && (
                <div>
                  <label className={`block text-sm font-bold mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Phone Number</label>
                  <input
                    type="tel"
                    value={withdrawalDetails.phoneNumber}
                    onChange={(e) => setWithdrawalDetails({ ...withdrawalDetails, phoneNumber: e.target.value })}
                    placeholder="+234..."
                    className={`w-full px-4 py-3 border-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none ${
                      theme === 'dark' ? 'bg-[#2a2a2c] text-white border-gray-700 placeholder-gray-500' : 'bg-white text-gray-900 border-gray-300'
                    }`}
                  />
                </div>
              )}
            </div>

            {/* Summary */}
            <div className={`p-4 rounded-lg border-2 mb-6 space-y-3 ${theme === 'dark' ? 'bg-[#2a2a2c] border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h2 className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Summary</h2>
              <div className="flex justify-between text-sm">
                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Amount</span>
                <span className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{parseFloat(amount) || 0} KXT</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Fee</span>
                <span className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{fee.toFixed(2)} KXT</span>
              </div>
              <div className={`flex justify-between text-sm pt-2 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className={`font-bold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>You Receive</span>
                <span className="font-bold text-green-500">{total.toFixed(2)} KXT</span>
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting || !amount || parseFloat(amount) <= 0}
              className="w-full bg-black dark:bg-blue-600 text-white py-4 rounded-lg text-sm font-extrabold hover:bg-gray-900 dark:hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</> : 'Confirm Withdrawal'}
            </button>
          </div>

          {/* ── Right: Withdrawal History ──────────────────────────────── */}
          <div className={`rounded-xl shadow-md p-8 ${theme === 'dark' ? 'bg-[#1a1a1c]' : 'bg-white'}`}>
            <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Withdrawal History
            </h2>

            {withdrawals.length === 0 ? (
              <div className="text-center py-12">
                <Clock className={`w-12 h-12 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                  No withdrawal requests yet
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {withdrawals.map((w) => (
                    <div key={w._id} className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className={`font-semibold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {w.amount} KXT
                          </p>
                          <p className={`text-xs mt-1 capitalize ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {w.withdrawalMethod?.replace('_', ' ')} · {new Date(w.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        {getStatusBadge(w.status)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {withdrawalPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                    <button
                      onClick={() => setWithdrawalPage(p => Math.max(1, p - 1))}
                      disabled={!withdrawalPagination.hasPrevious}
                      className={`text-sm font-medium disabled:opacity-50 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}
                    >
                      ← Previous
                    </button>
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Page {withdrawalPagination.currentPage} of {withdrawalPagination.totalPages}
                    </span>
                    <button
                      onClick={() => setWithdrawalPage(p => Math.min(withdrawalPagination.totalPages, p + 1))}
                      disabled={!withdrawalPagination.hasNext}
                      className={`text-sm font-medium disabled:opacity-50 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
